import { Card } from "@prisma/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { PageHeader } from "../../components/PageHeader";
import { QueryState } from "../../components/QueryState";
import { api, ApiError, call } from "../../lib/api";
import type { components } from "../../lib/api-types";
import { useSession } from "../../lib/session";

type Product = components["schemas"]["ProductOut"];
type Person = components["schemas"]["PersonOut"];
type User = components["schemas"]["UserOut"];

/** Admin (plan v2 /admin, M1-1): Preferred CP per product (Admin; Creative Lead may set this
 *  only) and the user list (Admin). The server decides every permission (F-05). */
export function AdminPage() {
  const { t } = useTranslation();
  const me = useSession();
  const qc = useQueryClient();
  const products = useQuery({ queryKey: ["products"], queryFn: () => call<Product[]>(api.GET("/app/v1/products")) });
  const cps = useQuery({
    queryKey: ["admin", "cps"],
    queryFn: () => call<Person[]>(api.GET("/app/v1/admin/creative-producers")),
  });
  const users = useQuery({
    queryKey: ["admin", "users"],
    queryFn: () => call<User[]>(api.GET("/app/v1/admin/users")),
    enabled: Boolean(me.permissions["admin.users.view"]),
  });
  const setCp = useMutation({
    mutationFn: (v: { id: string; preferred_cp_id: string | null; row_version: number }) =>
      call(api.PATCH("/app/v1/admin/products/{product_id}", {
        params: { path: { product_id: v.id } },
        body: { preferred_cp_id: v.preferred_cp_id, row_version: v.row_version },
      })),
    onSettled: () => qc.invalidateQueries({ queryKey: ["products"] }),
  });
  const errorCode = setCp.error instanceof ApiError ? setCp.error.code : null;

  return (
    <div className="space-y-8">
      <PageHeader titleKey="admin.title" subtitle={t("admin.subtitle")} />
      <Card className="p-6">
        <h2 className="text-[18px] font-semibold text-ink">{t("admin.preferred_cp_title")}</h2>
        <p className="mt-1 text-[14px] text-ink-2">{t("admin.preferred_cp_hint")}</p>
        {errorCode && (
          <p role="alert" className="mt-3 text-[14px] text-[var(--status-danger)]">
            {t(`error.${errorCode}`, { defaultValue: t("error.unknown") })}
          </p>
        )}
        <QueryState query={products} emptyKey="products.empty">
          {(rows) => (
            <table className="mt-5 w-full text-left text-[14px]">
              <thead className="text-[12px] uppercase tracking-wide text-ink-2">
                <tr>
                  <th className="py-2 font-medium">{t("admin.col_product")}</th>
                  <th className="py-2 font-medium">{t("products.preferred_cp")}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => (
                  <tr key={p.id} className="border-t border-line">
                    <td className="py-3 text-ink">
                      <span className="font-medium">{p.name}</span>
                      <span className="ml-2 text-ink-2">{p.code}</span>
                    </td>
                    <td className="py-3">
                      <label className="sr-only" htmlFor={`cp-${p.id}`}>
                        {t("products.preferred_cp")}
                      </label>
                      <select
                        id={`cp-${p.id}`}
                        className="h-10 w-64 rounded-[var(--radius-control)] border border-line bg-surface px-3 text-ink"
                        value={p.preferred_cp_id ?? ""}
                        disabled={setCp.isPending}
                        onChange={(e) =>
                          setCp.mutate({ id: p.id, preferred_cp_id: e.target.value || null, row_version: p.row_version })
                        }
                      >
                        <option value="">{t("products.unassigned")}</option>
                        {(cps.data ?? []).map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </QueryState>
      </Card>
      {me.permissions["admin.users.view"] && (
        <Card className="p-6">
          <h2 className="text-[18px] font-semibold text-ink">{t("admin.users_title")}</h2>
          <QueryState query={users} emptyKey="admin.users_empty">
            {(rows) => (
              <table className="mt-5 w-full text-left text-[14px]">
                <thead className="text-[12px] uppercase tracking-wide text-ink-2">
                  <tr>
                    <th className="py-2 font-medium">{t("admin.col_name")}</th>
                    <th className="py-2 font-medium">{t("admin.col_roles")}</th>
                    <th className="py-2 font-medium">{t("admin.col_status")}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((u) => (
                    <tr key={u.id} className="border-t border-line text-ink">
                      <td className="py-3 font-medium">{u.name}</td>
                      <td className="py-3 text-ink-2">{u.roles.map((r) => t(`role.${r}`)).join(", ")}</td>
                      <td className="py-3 text-ink-2">{t(`admin.user_status_${u.status.toLowerCase()}`)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </QueryState>
        </Card>
      )}
    </div>
  );
}
